import React, {useEffect, useRef, useState} from 'react';
import {Box, Collapse, Flex, HStack, Skeleton, useTheme, keyframes} from '@chakra-ui/react';
import {TextG12, TextG14} from '../../../components/typography';
import useAuth from '../../../hooks/useAuth';
import useProfileFieldsStatus from '../../../hooks/useProfileFieldsStatus';
import {useConversationStarters} from '../../../hooks/useConversationStarters';
import {useAvailability} from '../../../hooks/useAvailability';
import {useCityProfile} from '../../../hooks/useCityProfile';

const fillIn = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const PROFILE_FIELDS = [
  {key: 'profile_photo', label: 'Photo'},
  {key: 'handle', label: 'Nickname'},
  {key: 'home_address', label: 'Address'},
  {key: 'about_me', label: 'Introduction'},
  {key: 'conversation_starters', label: 'Starters'},
  {key: 'availability', label: 'Availability'},
  {key: 'time_in_city', label: 'Time in City'},
] as const;

const ProfileProgress = () => {
  const theme = useTheme();
  const {user} = useAuth();
  const profileStatus = useProfileFieldsStatus();
  const [isExpanded, setIsExpanded] = useState(false);
  const {data: conversationStarters = [], isLoading: isLoadingStarters} = useConversationStarters();
  const {data: availability, isLoading: isLoadingAvailability} = useAvailability();
  const {data: cityProfile, isLoading: isLoadingCityProfile} = useCityProfile();

  const isLoading = isLoadingStarters || isLoadingAvailability || isLoadingCityProfile;

  // Animate segment fills only on the initial paint after data resolves.
  const [hasAnimated, setHasAnimated] = useState(false);
  const didAnimate = useRef(false);
  useEffect(() => {
    if (!isLoading && !didAnimate.current) {
      didAnimate.current = true;
      const t = setTimeout(() => setHasAnimated(true), 1200);
      return () => clearTimeout(t);
    }
  }, [isLoading]);

  const fieldCompleted: Record<string, boolean> = {
    profile_photo: profileStatus.profile_photo.has_value,
    handle: profileStatus.handle.has_value,
    home_address: profileStatus.home_address.has_value,
    about_me: !!user?.aboutMe && user.aboutMe.length > 0,
    conversation_starters: conversationStarters.length > 0,
    availability:
      (availability?.general?.length ?? 0) > 0 ||
      Object.values(availability?.specific ?? {}).some(
        (times) => Array.isArray(times) && times.length > 0,
      ),
    time_in_city: !!(cityProfile?.city_duration || cityProfile?.neighborhood_duration || cityProfile?.home_duration),
  };

  const completedCount = Object.values(fieldCompleted).filter(Boolean).length;
  const totalCount = PROFILE_FIELDS.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  if (isLoading) return null;
  if (progressPercent === 100) return null;

  return (
    <Box
      mx={4}
      mb={4}
      bg="white"
      borderRadius="16px"
      border="1px solid #EBEBEB"
      overflow="hidden"
      boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
      <HStack
        as="button"
        w="100%"
        justify="space-between"
        px={5}
        py={4}
        onClick={() => setIsExpanded(!isExpanded)}
        _active={{bg: '#FAFAFA'}}>
        {isLoading ? (
          <Skeleton height="18px" width="160px" borderRadius="4px" />
        ) : (
          <TextG14 mt="0" fontWeight="600">
            Profile Progress: {progressPercent}%
          </TextG14>
        )}
        <i
          className={`fi fi-rr-angle-small-${isExpanded ? 'up' : 'down'}`}
          aria-hidden="true"
          style={{fontSize: '16px', color: theme.colors.grey}}
        />
      </HStack>

      {/* Segmented progress bar (always visible) */}
      <Flex px={5} pb={4} gap="3px">
        {isLoading ? (
          PROFILE_FIELDS.map((field) => (
            <Skeleton key={field.key} flex={1} h="6px" borderRadius="3px" />
          ))
        ) : (
          PROFILE_FIELDS.map((field, i) => (
            <Box
              key={field.key}
              flex={1}
              h="6px"
              borderRadius="3px"
              bg="#E5E5E5"
              overflow="hidden">
              <Box
                h="100%"
                borderRadius="3px"
                bg={fieldCompleted[field.key] ? '#22C55E' : 'transparent'}
                transformOrigin="left"
                animation={
                  fieldCompleted[field.key] && !hasAnimated
                    ? `${fillIn} 0.5s ease-out ${i * 0.08}s both`
                    : undefined
                }
              />
            </Box>
          ))
        )}
      </Flex>

      <Collapse in={isExpanded} animateOpacity>
        <Box px={5} pb={4}>
          {PROFILE_FIELDS.map((field) => (
            <HStack key={field.key} justify="space-between" py={1.5}>
              <TextG12 mt="0" color={theme.colors.grey}>
                {field.label}
              </TextG12>
              {isLoading && ['conversation_starters', 'availability', 'time_in_city'].includes(field.key) ? (
                <Skeleton w="14px" h="14px" borderRadius="full" />
              ) : fieldCompleted[field.key] ? (
                <i
                  className="fi fi-rr-check-circle"
                  aria-label={`${field.label} complete`}
                  role="img"
                  style={{fontSize: '14px', color: '#22C55E'}}
                />
              ) : (
                <Box w="14px" h="14px" borderRadius="full" border="1.5px solid #E5E5E5" aria-label={`${field.label} incomplete`} role="img" />
              )}
            </HStack>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProfileProgress;
